// components/WhyChooseUs.js
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, Users, Award } from 'lucide-react';

const WhyChooseUs = () => {
  const features = [
    {
      icon: <CheckCircle />,
      title: 'Quality Assurance',
      description: 'Rigorous testing processes ensure bug-free delivery'
    },
    {
      icon: <Clock />,
      title: 'On-Time Delivery',
      description: 'We respect deadlines and deliver projects on time'
    },
    {
      icon: <Users />,
      title: 'Expert Team',
      description: 'Skilled professionals with years of experience'
    },
    {
      icon: <Award />,
      title: 'Award Winning',
      description: 'Recognized for excellence in technology services'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Why Choose <span className="text-blue-400">Raza Tech Solutions</span>
            </h2>
            <p className="text-gray-300 mb-8">
              We combine technical expertise with business understanding to deliver 
              solutions that drive real results. Our client-centric approach ensures 
              your vision becomes reality.
            </p>
            
            <div className="space-y-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-4"
                >
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-1">{feature.title}</h3>
                    <p className="text-gray-300">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-300">Project Completion</div>
                  <div className="text-lg font-bold">98%</div>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: '98%' }}
                    transition={{ duration: 1, delay: 0.5 }}
                    viewport={{ once: true }}
                    className="h-full bg-blue-500 rounded-full"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-300">Client Satisfaction</div>
                  <div className="text-lg font-bold">95%</div>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: '95%' }}
                    transition={{ duration: 1, delay: 0.7 }}
                    viewport={{ once: true }}
                    className="h-full bg-green-500 rounded-full"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-300">On-Time Delivery</div>
                  <div className="text-lg font-bold">96%</div>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: '96%' }}
                    transition={{ duration: 1, delay: 0.9 }}
                    viewport={{ once: true }}
                    className="h-full bg-yellow-500 rounded-full"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;